/**
 * Template Selection Modal Component
 * 
 * Modal for selecting fax templates with hierarchical dropdowns (Branch > Usecase > Sub-usecase).
 * Displays available template files based on selection and allows PDF preview.
 * 
 * @component
 * @example
 * return <TemplateSelectionModal onClose={handleClose} onSelectTemplate={handleSelect} />
 */

import React, { useState, useEffect } from "react";
import {
    Close as CloseIcon,
    Description as PdfIcon,
    ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { 
    Box, 
    Typography, 
    Button, 
    FormControl, 
    Select, 
    MenuItem, 
    List, 
    ListItem, 
    ListItemIcon, 
    ListItemText
} from "@mui/material";
import { useTheme } from "../../ThemeContext";
import FaxAgentService from "../../services/fax-agent-service";

const TemplateSelectionModal = ({
    onClose,
    onSelectTemplate,
    themeMode
}) => {
    const { currentTheme } = useTheme();
    
    // State for template structure
    const [templateStructure, setTemplateStructure] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Selection states
    const [selectedBranch, setSelectedBranch] = useState("");
    const [selectedUsecase, setSelectedUsecase] = useState("");
    const [selectedSubUsecase, setSelectedSubUsecase] = useState("");
    const [availableFiles, setAvailableFiles] = useState([]);

    // Filtered options based on selections
    const availableBranches = templateStructure?.branches || [];
    const availableUsecases = selectedBranch 
        ? availableBranches?.find(branch => branch.name === selectedBranch)?.usecases || []
        : [];
    const availableSubUsecases = selectedUsecase 
        ? availableUsecases?.find(usecase => usecase.name === selectedUsecase)?.subusecases || []
        : [];

    /**
     * Load template structure from API
     */
    useEffect(() => {
        loadTemplateStructure();
    }, []);

    const loadTemplateStructure = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log('🔄 Loading template structure...');
            const response = await FaxAgentService.getTemplateStructure();
            console.log('✅ Template structure loaded:', response);
            
            if (response.statusCode === "SUCCESS") {
                setTemplateStructure(response.response);
            } else {
                setError(response.message || 'Failed to load template structure');
            }
        } catch (error) {
            console.error('❌ Error loading template structure:', error);
            setError(error.message || 'Failed to load template structure');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle branch selection
     */
    const handleBranchChange = (branchName) => {
        setSelectedBranch(branchName);
        setSelectedUsecase("");
        setSelectedSubUsecase("");
        setAvailableFiles([]);
    };

    /**
     * Handle usecase selection
     */
    const handleUsecaseChange = (usecaseName) => {
        setSelectedUsecase(usecaseName);
        setSelectedSubUsecase("");
        setAvailableFiles([]);
    };

    /**
     * Handle sub-usecase selection
     */
    const handleSubUsecaseChange = (subUsecaseName) => {
        setSelectedSubUsecase(subUsecaseName);
        
        // Find and set available files for the selected sub-usecase
        const subUsecase = availableSubUsecases?.find(sub => sub.name === subUsecaseName);
        setAvailableFiles(subUsecase?.files || []);
    };

    /**
     * Handle template file selection
     */
    const handleFileSelect = (file) => {
        // Create a template object with dropdown selections and file data
        const templateData = {
            // File information
            name: file.name,
            size: file.size,
            type: file.content_type,
            fullPath: file.full_path,
            file: file, // Store the original file data
            
            // Dropdown selections for signed URL payload
            branch: selectedBranch,
            usecase: selectedUsecase,
            subusecase: selectedSubUsecase,
            filename: file.name
        };
        
        onSelectTemplate(templateData);
    };

    /**
     * Format file size
     */
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (loading) {
        return (
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 3000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <img
                    style={{ width: '100px', height: '100px' }}
                    src={themeMode === "dark" ? "/icons/loader_white.gif" : "/icons/loader_black.gif"}
                    alt="Loading"
                />
            </div>
        );
    }

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 3000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Box
                sx={{
                    width: "500px",
                    maxHeight: "80vh",
                    backgroundColor: themeMode === 'dark' ? "#1A1B3A" : "#ffffff",
                    borderRadius: "12px",
                    boxShadow: themeMode === 'dark' ? "0 12px 32px rgba(0,0,0,0.6)" : "0 12px 32px rgba(0,0,0,0.15)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        padding: "24px",
                        borderBottom: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "20px",
                            fontWeight: 600,
                            color: themeMode === 'dark' ? "#fff" : "#000",
                        }}
                    >
                        Select Branch, Usecase & Sub-usecase
                    </Typography>
                    <CloseIcon
                        onClick={onClose}
                        sx={{
                            color: themeMode === 'dark' ? "#fff" : "#000",
                            cursor: "pointer",
                            fontSize: "24px",
                        }}
                    />
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        padding: "20px",
                        flex: 1,
                        overflowY: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                    }}
                >
                    {/* Error Message */}
                    {error && (
                        <Box
                            sx={{
                                padding: "12px",
                                backgroundColor: themeMode === 'dark' ? "#2d1b1b" : "#ffeaea",
                                border: `1px solid ${themeMode === 'dark' ? "#8b0000" : "#ff4d4f"}`,
                                borderRadius: "8px",
                                color: themeMode === 'dark' ? "#ff6b6b" : "#d32f2f",
                                fontSize: "14px",
                            }}
                        >
                            {error}
                        </Box>
                    )}

                    {/* Dropdowns */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {/* Branch Selection */}
                        <FormControl fullWidth>
                            <Typography
                                sx={{
                                    color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    marginBottom: "8px",
                                }}
                            >
                                Select Branch
                            </Typography>
                            <Select
                                value={selectedBranch}
                                onChange={(e) => handleBranchChange(e.target.value)}
                                displayEmpty
                                IconComponent={ExpandMoreIcon}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
                                            "& .MuiMenuItem-root": {
                                                color: themeMode === 'dark' ? "#fff" : "#000",
                                                "&:hover": {
                                                    backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                                                },
                                                "&.Mui-selected": {
                                                    backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.8)" : "rgba(245, 245, 245, 0.8)",
                                                    "&:hover": {
                                                        backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#302d4c" : "#e0e0e0",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                    },
                                    "& .MuiSelect-select": {
                                        color: themeMode === 'dark' ? "#fff" : "#000",
                                    },
                                    "& .MuiSelect-icon": {
                                        color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                    },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    <em>Select Branch</em>
                                </MenuItem>
                                {availableBranches?.map((branch) => (
                                    <MenuItem key={branch.name} value={branch.name}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Usecase Selection */}
                        <FormControl fullWidth disabled={!selectedBranch}>
                            <Typography
                                sx={{
                                    color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    marginBottom: "8px",
                                }}
                            >
                                Select Usecase
                            </Typography>
                            <Select
                                value={selectedUsecase}
                                onChange={(e) => handleUsecaseChange(e.target.value)}
                                displayEmpty
                                disabled={!selectedBranch}
                                IconComponent={ExpandMoreIcon}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
                                            "& .MuiMenuItem-root": {
                                                color: themeMode === 'dark' ? "#fff" : "#000",
                                                "&:hover": {
                                                    backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                                                },
                                                "&.Mui-selected": {
                                                    backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.8)" : "rgba(245, 245, 245, 0.8)",
                                                    "&:hover": {
                                                        backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#302d4c" : "#e0e0e0",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                    },
                                    "& .MuiSelect-select": {
                                        color: themeMode === 'dark' ? "#fff" : "#000",
                                    },
                                    "& .MuiSelect-icon": {
                                        color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                    },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    <em>Select Usecase</em>
                                </MenuItem>
                                {availableUsecases?.map((usecase) => (
                                    <MenuItem key={usecase.name} value={usecase.name}>
                                        {usecase.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Sub-usecase Selection */}
                        <FormControl fullWidth disabled={!selectedUsecase}>
                            <Typography
                                sx={{
                                    color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    marginBottom: "8px",
                                }}
                            >
                                Select Sub Usecase
                            </Typography>
                            <Select
                                value={selectedSubUsecase}
                                onChange={(e) => handleSubUsecaseChange(e.target.value)}
                                displayEmpty
                                disabled={!selectedUsecase}
                                IconComponent={ExpandMoreIcon}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
                                            "& .MuiMenuItem-root": {
                                                color: themeMode === 'dark' ? "#fff" : "#000",
                                                "&:hover": {
                                                    backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                                                },
                                                "&.Mui-selected": {
                                                    backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.8)" : "rgba(245, 245, 245, 0.8)",
                                                    "&:hover": {
                                                        backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#ffffff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#302d4c" : "#e0e0e0",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                    },
                                    "& .MuiSelect-select": {
                                        color: themeMode === 'dark' ? "#fff" : "#000",
                                    },
                                    "& .MuiSelect-icon": {
                                        color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                    },
                                }}
                            >
                                <MenuItem value="" disabled>
                                    <em>Select Sub Usecase</em>
                                </MenuItem>
                                {availableSubUsecases?.map((subUsecase) => (
                                    <MenuItem key={subUsecase.name} value={subUsecase.name}>
                                        {subUsecase.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Template Files List */}
                    {availableFiles?.length > 0 && (
                        <Box>
                            <Typography
                                sx={{
                                    color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    marginBottom: "16px",
                                }}
                            >
                                Select Template
                            </Typography>
                            <List
                                sx={{
                                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                                    borderRadius: "8px",
                                    border: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                }}
                            >
                                {availableFiles?.map((file, index) => (
                                    <ListItem
                                        key={index}
                                        onClick={() => handleFileSelect(file)}
                                        sx={{
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                                            },
                                            borderBottom: index < availableFiles?.length - 1 ? `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}` : "none",
                                        }}
                                    >
                                        <ListItemIcon>
                                            <PdfIcon sx={{ color: "#FF6B47", fontSize: "20px" }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    sx={{
                                                        color: themeMode === 'dark' ? "#fff" : "#000",
                                                        fontSize: "14px",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {file.name}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography
                                                    sx={{
                                                        color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    {formatFileSize(file.size)} • {(() => {
                                                        try {
                                                            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                                            let isoStr = file.modified;
                                                            if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(file.modified)) {
                                                                isoStr = file.modified + 'Z';
                                                            }
                                                            const date = new Date(isoStr);
                                                            if (isNaN(date.getTime())) return 'N/A';
                                                            return date.toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                                timeZone: userTimeZone
                                                            });
                                                        } catch (error) {
                                                            return 'N/A';
                                                        }
                                                    })()}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {/* No files message */}
                    {selectedSubUsecase && availableFiles?.length === 0 && (
                        <Box
                            sx={{
                                padding: "24px",
                                textAlign: "center",
                                color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                fontStyle: "italic",
                            }}
                        >
                            No template files available for this sub-usecase
                        </Box>
                    )}
                </Box>

            </Box>
        </div>
    );
};

export default TemplateSelectionModal;
